/**
 * Yleiskuva (/admin?instance=<id>): where one Kuutio stands. One card per
 * stage — Valmistelu, Toteutus, Tulokset — with a checklist built from real
 * data, the next step, and the move into the next stage when a stage is done.
 * The instance's current stage (from its status) is open. Below: every
 * screen, collapsed, and the services' health.
 * specs/admin-instance-lifecycle.md (#744). Replaces AdminHub's card launcher.
 *
 * Without an instance, /admin goes to the instance list.
 */
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import AdminHeader from '../../components/AdminHeader/AdminHeader';
import useAdminInstance, { readRememberedInstanceId } from '../../hooks/useAdminInstance';
import useInstanceRecord from '../../hooks/useInstanceRecord';
import useWorkshopChain from '../../hooks/useWorkshopChain';
import useInstanceOverview from '../../hooks/useInstanceOverview';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { getStatusLabel } from '../../utils/statusTranslations';
import { buildStageStatus, STAGE_ORDER, STATUS_FOR_STAGE } from '../../utils/stageStatus';
import StageCard from './StageCard';
import AllViewsList from './AllViewsList';
import ServiceHealth from './ServiceHealth';

// Where a stage move lands: the stage's working page.
const STAGE_HOME = { run: '/admin/workshop', results: '/reportgen' };

const Page = styled.div`
  min-height: var(--kuutio-vh, 100vh);
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
  color: #fff;
`;

const Column = styled.main`
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 24px 64px;

  @media (max-width: 600px) { padding: 16px 16px 48px; }

  h1 { margin: 0; font-size: 26px; }
  .lead { margin: 4px 0 20px; color: rgba(255, 255, 255, 0.6); font-size: 14px; }
`;

const Stages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Notice = styled.div`
  padding: 16px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.75);
`;

export default function AdminHome() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { instanceId } = useAdminInstance();
  const { instance, error: instanceError, setStatus } = useInstanceRecord(instanceId);
  const { chain, error: chainError } = useWorkshopChain(instanceId);
  const { overview, error: overviewError } = useInstanceOverview(instanceId);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState(null);

  // No instance in the URL and none remembered: pick one from the list.
  if (!instanceId && !readRememberedInstanceId()) return <Navigate to="/admin/instances" replace />;

  const error = instanceError?.message || overviewError || chainError;
  const status = instance && overview ? buildStageStatus({ instance, chain, overview }) : null;

  const move = async (nextStage) => {
    setMoving(true);
    setMoveError(null);
    try {
      await setStatus(STATUS_FOR_STAGE[nextStage]);
      navigate(`${STAGE_HOME[nextStage]}?instance=${instanceId}`);
    } catch (err) {
      setMoveError(err.message);
      setMoving(false);
    }
  };

  const date = instance?.metadata?.workshop_date;

  return (
    <Page>
      <AdminHeader instanceId={instanceId} instanceName={instance?.title} />
      <Column>
        <h1>{instance?.title || t('settingsPages.loading')}</h1>
        {instance && (
          <p className="lead">
            {getStatusLabel(instance.status, language)}
            {' · '}{(instance.language || 'fi').toUpperCase()}
            {/* metadata.workshop_date is free text ("7.–8.9.2026"), shown as written */}
            {date && ` · ${date}`}
          </p>
        )}

        {error && <Notice>{t('settingsPages.loadFailed', { error })}</Notice>}
        {moveError && <Notice>{t('settingsPages.saveFailed', { error: moveError })}</Notice>}
        {!error && !status && <Notice>{t('settingsPages.loading')}</Notice>}

        {status && (
          <Stages key={status.current}>
            {STAGE_ORDER.map((key, i) => (
              <StageCard
                key={key}
                stageKey={key}
                summary={status.stages[key]}
                current={status.current === key}
                instanceId={instanceId}
                nextStageKey={STAGE_ORDER[i + 1]}
                onMove={() => move(STAGE_ORDER[i + 1])}
                moving={moving}
              />
            ))}
          </Stages>
        )}

        {instanceId && <AllViewsList instanceId={instanceId} />}
        <ServiceHealth />
      </Column>
    </Page>
  );
}
